// Version Control Data Structure

export interface VersionIssue {
  id?: string; // Optional for backward compatibility, required for delete functionality
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
}

export interface VersionFeature {
  title: string;
  description: string;
  details?: string[];
}

export interface Version {
  version: string;
  releaseDate: string;
  baseCommit: string;
  releaseCommit: string;
  status: 'latest' | 'stable' | 'deprecated';
  summary: string;

  // Statistics
  commits: number;
  filesChanged: number;
  linesAdded: number;
  linesRemoved: number;

  // Features and changes
  majorFeatures: VersionFeature[];
  bugFixes: string[];
  knownIssues: VersionIssue[];

  // Machine deployment
  deployedMachines: string[];

  // Additional metadata
  pullRequests?: string[];
  breakingChanges?: string[];
}

export const versionData: Version[] = [
  {
    version: 'v1.1.2',
    releaseDate: 'November 17, 2025',
    baseCommit: 'f1dd5ee',
    releaseCommit: '784d777',
    status: 'latest',
    summary: 'Hotfix release addressing critical auto-dispense bug and popup sizing improvements',

    commits: 31,
    filesChanged: 2,
    linesAdded: 11,
    linesRemoved: 5,

    majorFeatures: [
      {
        title: 'Auto-Dispense Fix',
        description: 'Fixed critical bug where 950ml and 450ml auto-dispense buttons were not stopping at target volumes',
        details: [
          'Button visibility corrected - only visible when idle',
          'Timeout management improved to prevent premature clearing',
          'UI synchronization enhanced with 200ms delay',
          'Race conditions in UI state updates eliminated'
        ]
      },
      {
        title: 'Popup Sizing Enhancement',
        description: 'Improved popup positioning and responsive behavior',
        details: [
          'Added flexbox centering for better alignment',
          'Consistent 50px margins on each side',
          'Better vertical and horizontal centering'
        ]
      }
    ],

    bugFixes: [
      'Fixed auto-dispense not stopping at target volumes (Issue #109)',
      'Corrected timeout management in dispense operations',
      'Improved popup positioning and sizing'
    ],

    knownIssues: [
      {
        description: 'PR #103 features reverted (sensor validation, descaling UI) - to be re-evaluated for future releases',
        severity: 'medium'
      }
    ],

    deployedMachines: [],

    pullRequests: ['#103 (reverted by #105)', '#109'],
    breakingChanges: []
  },

  {
    version: 'v1.1.1',
    releaseDate: 'October 27, 2025',
    baseCommit: 'e87ab08',
    releaseCommit: 'f1dd5ee',
    status: 'stable',
    summary: 'Major feature expansion with service mode, demo processes, enhanced UI, and CI/CD infrastructure',

    commits: 175,
    filesChanged: 99,
    linesAdded: 5801,
    linesRemoved: 1996,

    majorFeatures: [
      {
        title: 'Service Mode Implementation',
        description: 'Complete service and diagnostics interface for machine maintenance and testing',
        details: [
          'Sensor testing interface with real-time monitoring',
          'Component testing for manual actuator control',
          'Function testing to execute individual machine operations',
          'Passkey authentication system for secure access',
          'New backend module: service_sensor.py (404 lines)'
        ]
      },
      {
        title: 'Demo Process Mode',
        description: 'New demonstration workflows for showcasing machine capabilities',
        details: [
          'preclean_demo() - Simplified preclean demonstration',
          'production_demo() - Abbreviated production cycle (5-10 min vs 2+ hours)',
          'add_oats_demo() - Oat addition demonstration',
          'cleaning_demo() - Cleaning process demonstration',
          'Safe testing without full production commitment'
        ]
      },
      {
        title: 'Language Switching Infrastructure',
        description: 'Dynamic language switching between German and English',
        details: [
          'language-handler.js (92 lines) for dynamic switching',
          'Language preference storage',
          'Both German and English frontends support switching'
        ]
      },
      {
        title: 'Oat Customization Interface',
        description: 'Custom oat mixture configuration and user preferences',
        details: [
          'Visual customization interface (oat_ui.html - 323 lines)',
          'User preference settings',
          'Custom oat mixture configuration'
        ]
      },
      {
        title: 'CI/CD Infrastructure',
        description: 'GitHub Actions workflows for automated testing and quality checks',
        details: [
          'Automated code style checking with ruff',
          'Code formatting enforcement',
          'Unit test automation'
        ]
      },
      {
        title: 'Enhanced Backend Robustness',
        description: 'Improved production workflows and error handling',
        details: [
          'Modified drain_batch_fully with enhanced drainage logic',
          'More robust full_batch operation',
          'Dynamic flow rate adjustment',
          'Better E01 and E13 error handling'
        ]
      }
    ],

    bugFixes: [
      'Enable pin removal fix (PR #48)',
      'E13 error hotfix on startup (PR #73)',
      'Pin swap fix - purge pump and valve_nozzle_2',
      'Oat position configuration fix (PR #76)',
      'Settings page bug fix',
      'Zone.Identifier cleanup (Windows metadata)',
      'Aramark E9 driptray overflow prevention'
    ],

    knownIssues: [
      {
        description: 'Skip button implementation in progress, functionality to be modified',
        severity: 'low'
      },
      {
        description: 'drain_batch_fully max cycles needs verification',
        severity: 'medium'
      },
      {
        description: 'Passkey is hard-coded in codebase',
        severity: 'high'
      }
    ],

    deployedMachines: ['E60'],

    pullRequests: ['#37', '#48', '#73', '#76', '#79', '#85'],
    breakingChanges: []
  },

  {
    version: 'v1.0.1',
    releaseDate: 'July 3, 2025',
    baseCommit: '2f79658',
    releaseCommit: 'e87ab08',
    status: 'stable',
    summary: 'Major feature update with internationalization, enhanced sensor validation, and improved batch handling',

    commits: 57,
    filesChanged: 85,
    linesAdded: 6438,
    linesRemoved: 378,

    majorFeatures: [
      {
        title: 'English Language Support',
        description: 'Complete English frontend interface alongside existing German interface',
        details: [
          'New frontend_en/ directory with full English UI',
          '19+ HTML files for all screens',
          'Complete CSS styling and JavaScript modules',
          'German set as default, English available via settings'
        ]
      },
      {
        title: 'Advanced Sensor Validation System',
        description: 'New comprehensive sensor validation module for improved reliability',
        details: [
          'sensor_validation.py (1,261 lines)',
          'Validates driptray level sensors and cleaning shield position',
          'Sensor reliability tracking with fallback strategies',
          'Integration with main production flow'
        ]
      },
      {
        title: 'Enhanced Batch Management',
        description: 'Improved full_batch and full_batch_hot functions',
        details: [
          'Dynamic flow rate calculation at batch start',
          'Adapts to varying water pressure conditions',
          'drain_batch_fully() function for complete drainage',
          'Prevents residue buildup and contamination'
        ]
      },
      {
        title: 'Driptray Management Improvements',
        description: 'Enhanced overflow prevention and monitoring',
        details: [
          'Aramark E9 specific calibration',
          'Increased batch amount threshold',
          'Flow sensor validation during water addition',
          'Warning system for cleaning shield status'
        ]
      },
      {
        title: 'Add Water Functionality Enhancements',
        description: 'Automated time calculation and pre-batch water addition',
        details: [
          'Water addition time based on flow rate',
          'More accurate water dosing',
          'Delays before full_batch operations'
        ]
      }
    ],

    bugFixes: [
      'Fixed merge conflict markers from GitHub web UI',
      'Fixed frontend errors from merging',
      'Multiple PR review fixes',
      'Aramark E9 driptray overflow prevention',
      'False driptray error warnings fixed',
      'Heating rate monitoring improvements'
    ],

    knownIssues: [
      {
        description: 'drain_batch_fully max cycles set to 20 (insufficient for full batch - should be 60)',
        severity: 'high'
      },
      {
        description: 'Machine-specific configurations require individual calibration',
        severity: 'medium'
      }
    ],

    deployedMachines: ['E60', 'E9'],

    pullRequests: ['#1', '#4', '#5', '#6', '#7', '#8'],
    breakingChanges: []
  },

  {
    version: 'v1.0.0',
    releaseDate: 'April 1, 2025',
    baseCommit: '2f79658',
    releaseCommit: '2f79658',
    status: 'deprecated',
    summary: 'Initial baseline release',

    commits: 0,
    filesChanged: 0,
    linesAdded: 0,
    linesRemoved: 0,

    majorFeatures: [
      {
        title: 'Initial Release',
        description: 'Baseline production system',
        details: [
          'Core production workflows',
          'Basic sensor integration',
          'German frontend only',
          'Standard batch operations'
        ]
      }
    ],

    bugFixes: [],
    knownIssues: [],
    deployedMachines: ['E5'],
    pullRequests: [],
    breakingChanges: []
  }
];

// Helper function to get version by version string
export const getVersionByString = (versionString: string): Version | undefined => {
  return versionData.find(v => v.version === versionString);
};

// Helper function to get machines with specific version
export const getMachinesWithVersion = (versionString: string): string[] => {
  const version = getVersionByString(versionString);
  return version?.deployedMachines || [];
};

// Helper function to compare versions
export const compareVersions = (v1: string, v2: string): number => {
  const idx1 = versionData.findIndex(v => v.version === v1);
  const idx2 = versionData.findIndex(v => v.version === v2);
  return idx2 - idx1; // Reverse order (latest first)
};
